import { ABSENCE_STATUSES } from '@hirebotiq-ops/core';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/absences')({
  component: AdminAbsences,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type StatusFilter = 'all' | (typeof ABSENCE_STATUSES)[number];

function AdminAbsences() {
  const utils = trpc.useUtils();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const settings = trpc.settings.get.useQuery();
  const list = trpc.absences.list.useQuery(status === 'all' ? {} : { status });
  const review = trpc.absences.review.useMutation({
    onSuccess: () => {
      utils.absences.list.invalidate();
      toast.success('Antrag bearbeitet.');
    },
    onError: (e) => toast.error(e.message),
  });

  const showDecisionSupport = settings.data?.enableAbsenceDecisionSupport ?? true;

  return (
    <AdminLayout>
      <div className="space-y-4">
        <header>
          <h1 className="text-3xl font-bold">Abwesenheiten</h1>
          <p className="text-[var(--muted-foreground)]">Urlaub, Krankheit, Sonderurlaub — Freigabe mit Decision Support</p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Filter</CardTitle>
          </CardHeader>
          <CardContent className="max-w-xs">
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {ABSENCE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Zeitraum</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">Lade …</TableCell>
                  </TableRow>
                )}
                {list.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">Keine Anträge.</TableCell>
                  </TableRow>
                )}
                {list.data?.map((a) => (
                  <Fragment key={a.id}>
                    <TableRow>
                      <TableCell className="font-medium">
                        {[a.workerFirstName, a.workerLastName].filter(Boolean).join(' ') || '—'}
                      </TableCell>
                      <TableCell>{a.type}</TableCell>
                      <TableCell className="text-xs text-[var(--muted-foreground)]">
                        {a.startDate} – {a.endDate}
                        {a.reason ? <div className="mt-1">{a.reason}</div> : null}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={a.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2 items-center">
                          {showDecisionSupport && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setExpandedId((curr) => (curr === a.id ? null : a.id))}
                            >
                              {expandedId === a.id ? 'Prüfung zu' : 'ℹ️ Details & Saldo'}
                            </Button>
                          )}
                          {a.status === 'requested' && (
                            <>
                              <Button
                                size="sm"
                                disabled={review.isPending}
                                onClick={() => review.mutate({ absenceId: a.id, action: 'approve' })}
                              >
                                Freigeben
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={review.isPending}
                                onClick={() => review.mutate({ absenceId: a.id, action: 'reject' })}
                              >
                                Ablehnen
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {showDecisionSupport && expandedId === a.id && (
                      <TableRow>
                        <TableCell colSpan={5} className="p-3 bg-[var(--muted)]/10">
                          <AbsenceDecisionCard
                            workerId={a.workerId}
                            absenceId={a.id}
                            startDate={a.startDate}
                            endDate={a.endDate}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function AbsenceDecisionCard({
  workerId,
  absenceId,
  startDate,
  endDate,
}: {
  workerId: string;
  absenceId: string;
  startDate: string;
  endDate: string;
}) {
  const quota = trpc.absences.getQuota.useQuery({ workerId });
  const conflicts = trpc.absences.checkConflicts.useQuery({
    startDate,
    endDate,
    excludeAbsenceId: absenceId,
  });

  return (
    <div className="rounded-md border p-3 bg-[var(--card)] space-y-3 text-xs">
      <div className="font-semibold text-sm">🧠 Disponenten Decision Support (Live-Analyse)</div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <div className="font-medium text-[var(--muted-foreground)]">Urlaubs-Saldo des Workers:</div>
          {quota.isLoading && <div>Lade Saldo …</div>}
          {quota.data && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="bg-[var(--muted)] px-2 py-1 rounded">
                Verbleibend: <strong>{quota.data.remainingDays} Tage</strong>
              </span>
              <span className="bg-[var(--muted)] px-2 py-1 rounded">
                Genehmigt: <strong>{quota.data.approvedVacationDays} Tage</strong>
              </span>
              <span className="bg-[var(--muted)] px-2 py-1 rounded">
                Krankheit: <strong>{quota.data.sickDays} Tage</strong>
              </span>
            </div>
          )}
        </div>

        <div className="space-y-1">
          <div className="font-medium text-[var(--muted-foreground)]">Terminüberschneidungen:</div>
          {conflicts.isLoading && <div>Prüfe Konflikte …</div>}
          {conflicts.data && (
            <div>
              {!conflicts.data.hasAssignmentConflict && !conflicts.data.hasAbsenceConflict && (
                <span className="text-[oklch(0.55_0.15_145)] font-semibold flex items-center gap-1">
                  ✓ Keine Überschneidungen vorhanden.
                </span>
              )}
              {conflicts.data.hasAssignmentConflict && (
                <div className="text-[oklch(0.45_0.15_85)] font-medium">
                  ⚠️ Einsatz-Konflikt: Worker ist verplant bei{' '}
                  {conflicts.data.conflictingAssignments.map((a) => a.clientCompanyName).join(', ')}.
                </div>
              )}
              {conflicts.data.hasAbsenceConflict && (
                <div className="text-[oklch(0.45_0.15_85)] font-medium">
                  ⚠️ Abwesenheits-Konflikt mit anderem Antrag im Zeitraum.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

